import { CustomerAccountItem, LoadState, ViewItems } from '@inyova/models';

import { Action, createReducer, createSelector, on } from '@ngrx/store';
import * as AccountActions from '@account/account.actions';
import * as RootActions from '@app/app.actions';

import { BiometricLogin, State } from '@shared/models/State';

import { FundBreakdown } from './shared/models/Home';

export const initialState: RootState = {
  biometricLogin: {
    available: false,
    name: 'biometric',
    type: null
  },
  selectedAccount: null,
  isCrowdInvestorAccountActive: false,
  accountToFundBreakdown: {},
  fundBreakdownLoadedLanguage: null,
  viewedItems: {
    items: [],
    loadState: 'INIT'
  }
};

const rootReducer = createReducer(
  initialState,
  on(RootActions.setBiometricLogin, (state, action) => ({
    ...state,
    biometricLogin: action.payload
  })),
  on(RootActions.setSelectedAccount, (state, action) => ({
    ...state,
    selectedAccount: action.account
  })),
  on(RootActions.setCrowdInvestorAccountStatus, (state, action) => ({
    ...state,
    isCrowdInvestorAccountActive: action.active
  })),
  on(AccountActions.customerLogout, () => ({ ...initialState })),
  on(AccountActions.customerLogoutAndUnpairDevice, () => ({ ...initialState })),
  on(
    RootActions.fetchFundBreakdownForAccount,
    (state, { accountID }): RootState => ({
      ...state,
      accountToFundBreakdown: {
        ...state.accountToFundBreakdown,
        [accountID]: { stocks: [], cash: 0, status: 'LOADING' }
      }
    })
  ),
  on(
    RootActions.fetchFundBreakdownForAccountSuccess,
    (state, { accountID, stocks, cash, language }): RootState => ({
      ...state,
      accountToFundBreakdown: {
        ...state.accountToFundBreakdown,
        [accountID]: { stocks, cash, status: 'LOADED' }
      },
      ...((state.fundBreakdownLoadedLanguage === null || state.fundBreakdownLoadedLanguage !== language) && {
        fundBreakdownLoadedLanguage: language
      })
    })
  ),
  on(RootActions.fetchFundBreakdownForAccountFail, (state, { accountId }): RootState => {
    const { [accountId]: _, ...accountToFundBreakdown } = state.accountToFundBreakdown;
    return {
      ...state,
      accountToFundBreakdown
    };
  }),
  on(RootActions.getViewedItems, (state): RootState => {
    return state.viewedItems.loadState === 'LOADED' ? state : { ...state, viewedItems: { items: [], loadState: 'LOADING' } };
  }),
  on(
    RootActions.getViewedItemsSuccess,
    (state, { viewItemsList }): RootState => ({ ...state, viewedItems: { items: viewItemsList, loadState: 'LOADED' } })
  ),
  on(
    RootActions.getViewedItemsFail,
    (state): RootState => ({ ...state, viewedItems: { items: [], loadState: { error: 'Failed to get viewed items' } } })
  ),
  on(
    RootActions.setItemAsViewedSuccess,
    (state, { item }): RootState => ({ ...state, viewedItems: { items: [...state.viewedItems.items, item], loadState: state.viewedItems.loadState } })
  ),
  on(RootActions.unsetItemAsViewedSuccess, (state, { item }): RootState => {
    const currentItemsList = state.viewedItems.items;
    const itemIndex = currentItemsList.indexOf(item);

    if (itemIndex === -1) {
      return state;
    }

    return {
      ...state,
      viewedItems: {
        items: [...currentItemsList.slice(0, itemIndex), ...currentItemsList.slice(itemIndex + 1)],
        loadState: state.viewedItems.loadState
      }
    };
  }),
  on(
    RootActions.unsetItemAsViewedFail,
    (state): RootState => ({ ...state, viewedItems: { items: [], loadState: { error: 'Failed to unset viewed item' } } })
  )
);

export function reducer(state: RootState | undefined, action: Action) {
  return rootReducer(state, action);
}

export interface RootState {
  biometricLogin: BiometricLogin;
  selectedAccount: CustomerAccountItem;
  isCrowdInvestorAccountActive: boolean;
  accountToFundBreakdown: { [key: string]: FundBreakdown };
  fundBreakdownLoadedLanguage: string | null;
  viewedItems: {
    items: string[];
    loadState: LoadState;
  };
}

/*
 * SELECTORS
 */
export const selectFeature = (state: State) => state.root;
// Root reducer selectors
export const selectBiometricLogin = createSelector(selectFeature, (state: RootState) => state.biometricLogin);
export const selectSelectedAccount = createSelector(selectFeature, (state: RootState) => state.selectedAccount);
export const selectIsGrowAccountActive = createSelector(selectSelectedAccount, (account: CustomerAccountItem) => account?.kind === 'grow');
export const selectCrowdInvestorAccountActive = createSelector(selectFeature, (state: RootState) => state?.isCrowdInvestorAccountActive);
export const selectAccountToFundBreakdown = createSelector(selectFeature, (state: RootState) => state.accountToFundBreakdown);
export const selectFundBreakdown = createSelector(
  selectAccountToFundBreakdown,
  selectSelectedAccount,
  (accountToFundBreakdown, currentAccount): FundBreakdown | null => (currentAccount ? (accountToFundBreakdown[currentAccount.id] ?? null) : null)
);
export const selectFundBreakdownLoadedLanguage = createSelector(selectFeature, (state: RootState) => state.fundBreakdownLoadedLanguage);

export const selectViewedItems = createSelector(selectFeature, (state: RootState) => state.viewedItems);
export const selectViewedItemsList = createSelector(selectViewedItems, ({ items }) => items);
export const selectCustomerViewedItem = (viewItem: ViewItems) => createSelector(selectViewedItemsList, (items: string[]) => items.includes(viewItem));
export const selectViewedItemsLoadState = createSelector(selectViewedItems, ({ loadState }) => loadState);
